'use client';

import { FC, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, ChevronDown, ArrowRight } from 'lucide-react';
import HotdeskButton from './HotdeskButton';
import styles from './SubscriptionPlansSection.module.scss';

/* ── Plan / FAQ shapes ── */
interface Plan {
  name: string;
  description: string;
  price: string;
  badge?: string;
  features: string[];
  cta: string;
  ctaLink?: string;
  highlighted?: boolean;
}
interface Faq {
  question: string;
  answer: string;
}
export interface SubscriptionPlansSectionProps {
  className?: string;
}
const SubscriptionPlansSection: FC<SubscriptionPlansSectionProps> = ({
  className
}) => {
  const t = useTranslations('subscriptionPlans');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const plans: Plan[] = [{
    name: t('plans.starter.name'),
    description: t('plans.starter.desc'),
    price: t('plans.starter.price'),
    features: [t('plans.starter.features.0'), t('plans.starter.features.1'), t('plans.starter.features.2'), t('plans.starter.features.3')],
    cta: t('plans.starter.cta')
  }, {
    name: t('plans.pro.name'),
    description: t('plans.pro.desc'),
    price: t('plans.pro.price'),
    badge: t('plans.pro.badge'),
    features: [t('plans.pro.features.0'), t('plans.pro.features.1'), t('plans.pro.features.2'), t('plans.pro.features.3')],
    cta: t('plans.pro.cta'),
    highlighted: true
  }, {
    name: t('plans.max.name'),
    description: t('plans.max.desc'),
    price: t('plans.max.price'),
    features: [t('plans.max.features.0'), t('plans.max.features.1'), t('plans.max.features.2'), t('plans.max.features.3')],
    cta: t('plans.max.cta')
  }, {
    name: t('plans.enterprise.name'),
    description: t('plans.enterprise.desc'),
    price: t('plans.enterprise.price'),
    badge: t('plans.enterprise.badge'),
    features: [t('plans.enterprise.features.0'), t('plans.enterprise.features.1'), t('plans.enterprise.features.2'), t('plans.enterprise.features.3'), t('plans.enterprise.features.4')],
    cta: t('plans.enterprise.cta'),
    ctaLink: '/contact'
  }];
  const faqs: Faq[] = [{
    question: t('faq.0.question'),
    answer: t('faq.0.answer')
  }, {
    question: t('faq.1.question'),
    answer: t('faq.1.answer')
  }, {
    question: t('faq.2.question'),
    answer: t('faq.2.answer')
  }];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <section className={`${styles.section} ${className ?? ''}`} data-sentry-component="SubscriptionPlansSection" data-sentry-source-file="SubscriptionPlansSection.tsx">
            {/* Pricing Cards */}
            <div className={styles.pricingHeader}>
                <h2 className={styles.title}>{t('title')}</h2>
                <p className={styles.subtitle}>{t('subtitle')}</p>
            </div>

            <div className={styles.cardsContainer}>
                {plans.map((plan, index) => <div key={index} className={`${styles.card} ${plan.highlighted ? styles.cardHighlighted : ''}`}>
                        {plan.badge && <div className={styles.badge}>
                                <span className={styles.badgeText}>
                                    {plan.badge.toUpperCase()}
                                </span>
                            </div>}
                        <h3 className={styles.planName}>{plan.name}</h3>
                        <p className={styles.planDesc}>{plan.description}</p>
                        <p className={styles.planPrice}>{plan.price}</p>

                        <ul className={styles.featureList}>
                            {plan.features.map((feature, fIndex) => <li key={fIndex} className={styles.featureItem}>
                                    <Check size={14} className={styles.checkIcon} />
                                    <span>{feature}</span>
                                </li>)}
                        </ul>

                        {plan.ctaLink ? <HotdeskButton as="next-link" href={plan.ctaLink} variant="primary" size="md" fullWidth className={styles.ctaButton}>
                                {plan.cta}
                            </HotdeskButton> : <HotdeskButton variant="primary" size="md" fullWidth className={styles.ctaButton}>
                                {plan.cta}
                            </HotdeskButton>}
                    </div>)}
            </div>

            {/* FAQ Section */}
            {faqs.length > 0 && <div className={styles.faqSection}>
                    <p className={styles.faqSubtitle}>{t('faqSubtitle')}</p>
                    <h3 className={styles.faqTitle}>{t('faqTitle')}</h3>

                    <div className={styles.faqList}>
                        {faqs.map((faq, index) => <div key={index} className={`${styles.faqItem} ${openFaq === index ? styles.faqItemOpen : ''}`}>
                                <button className={styles.faqQuestion} onClick={() => toggleFaq(index)} aria-expanded={openFaq === index}>
                                    <span>{faq.question}</span>
                                    <ChevronDown size={18} className={`${styles.chevron} ${openFaq === index ? styles.chevronOpen : ''}`} />
                                </button>
                                {openFaq === index && <div className={styles.faqAnswer}>
                                        <p>{faq.answer}</p>
                                    </div>}
                            </div>)}
                    </div>

                    <div className={styles.faqCta}>
                        <HotdeskButton as="next-link" href="/memberships" variant="primary" size="md" iconRight={<ArrowRight size={16} />} className={styles.faqCtaButton}>
                            {t('viewAllCta')}
                        </HotdeskButton>
                    </div>
                </div>}
        </section>;
};
export default SubscriptionPlansSection;